import React from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { publicApiClient } from "./api";
import { App } from "./App";
import { useAuthConfigQuery } from "./data";
import { clerkAppearance } from "./lib/clerkAppearance";
import { Toaster } from "./notify/Toaster";
import "./styles.css";

// AIF Path B Wave 1 defaults, used verbatim. See:
//   apps/web/src/main.tsx in AIF.
// staleTime 10s              — queries are reasonably fresh between refetches
// refetchOnWindowFocus true  — snap fresh when operators tab back in
// retry 2                    — absorb transient network hiccups
//
// NOTE: there is no `refetchInterval` here and no `setInterval` anywhere
// in the SPA. That is intentional — the polling-heavy default model is
// exactly what AIF Wave 2 unwound, and what this slice doc explicitly
// forbids.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: 2,
    },
  },
});

function MissingClerkConfig({ detail }: { detail?: string }) {
  return (
    <main className="auth-shell">
      <section className="auth-panel">
        <p className="eyebrow">GHL-Lite</p>
        <h1>Clerk publishable key required</h1>
        <p className="auth-copy">
          Configure <code>CLERK_PUBLISHABLE_KEY</code> and <code>CLERK_SECRET_KEY</code>
          on the web console service. The console backend is Clerk-gated, so the SPA
          intentionally does not provide an anonymous mode.
        </p>
        {detail ? <p className="panel-error">{detail}</p> : null}
      </section>
    </main>
  );
}

function getCurrentOrigin(): string | undefined {
  return typeof window !== "undefined" ? window.location.origin : undefined;
}

function resolveAllowedRedirectOrigins(): string[] {
  // Convergence with the AIF pattern: the prebuilt <SignIn> component lives
  // on the same origin the SPA is served from, so the only allowed redirect
  // origin is whatever the browser currently reports. Listed explicitly so
  // Clerk's allowedRedirectOrigins guard does not reject in-app navigations
  // after sign-in.
  const origin = getCurrentOrigin();
  return origin ? [origin] : [];
}

function ClerkRuntimeProvider() {
  const authConfig = useAuthConfigQuery(publicApiClient);

  if (authConfig.isLoading) {
    return (
      <main className="boot-shell">
        <p className="muted">Loading auth configuration…</p>
      </main>
    );
  }

  if (authConfig.error || !authConfig.data?.clerk_publishable_key) {
    return (
      <>
        <MissingClerkConfig
          detail={authConfig.error instanceof Error ? authConfig.error.message : undefined}
        />
        <Toaster />
      </>
    );
  }

  const signInUrl = authConfig.data.clerk_sign_in_url || "/sign-in";
  const afterSignOutUrl = authConfig.data.clerk_after_sign_out_url || signInUrl;

  // Match the AIF provider shape so the prebuilt <SignIn> handles Client
  // Trust, MFA, and verification natively. Falling back to the current
  // origin keeps deep-links inside the console after sign-in.
  return (
    <ClerkProvider
      publishableKey={authConfig.data.clerk_publishable_key}
      appearance={clerkAppearance}
      afterSignOutUrl={afterSignOutUrl}
      signInUrl={signInUrl}
      signInForceRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      signUpForceRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      allowedRedirectOrigins={resolveAllowedRedirectOrigins()}
    >
      <App />
      <Toaster />
    </ClerkProvider>
  );
}

const root = document.getElementById("root");
if (!root) {
  throw new Error("missing #root mount point in index.html");
}

createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ClerkRuntimeProvider />
    </QueryClientProvider>
  </React.StrictMode>,
);
